import { api } from "@semo/api-contracts";

/**
 * 상품 이미지 업로드 / 이미지 URL 검증
 *
 * 배경: 예전 ProductForm 의 「파일 업로드」탭은 `URL.createObjectURL(file)` 로 만든
 * `blob:` URL 을 그대로 폼에 넣고 끝냈다. 미리보기는 보이니 저장은 성공한 것처럼 보이지만
 * `blob:` 은 그 탭 안에서만 유효한 주소라, 새로고침하는 순간 죽은 링크가 된다. 실제로 운영
 * DB 에 그렇게 저장된 행이 남아 있었다. 그래서 업로드는 반드시 서버를 거쳐야 한다.
 *
 * 「URL 입력」탭도 검증이 없어서 쿠팡·알리 같은 *상품 상세 페이지* 링크가 그대로 저장됐다.
 * HTML 페이지는 <img> 에서 절대 렌더되지 않으므로 여기서 걸러낸다.
 */

/** 서버(`assets` 버킷)와 동일한 상한. 초과분은 요청 전에 잘라낸다. */
export const PRODUCT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** 서버 FilenameDomainService 의 assets 버킷 허용 확장자와 동일하게 유지할 것. */
export const PRODUCT_IMAGE_EXTENSIONS: readonly string[] = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
];

export interface ProductImageUploadResult {
  imageUrl: string;
  filePath: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
}

interface UploadEnvelope {
  data?: Partial<ProductImageUploadResult>;
}

/**
 * 이미지를 서버에 영구 업로드하고 공개 URL 을 돌려준다.
 *
 * @throws Error 사용자에게 그대로 보여줄 수 있는 한국어 메시지
 */
export async function uploadProductImage(
  file: File,
): Promise<ProductImageUploadResult> {
  const extension = file.name
    .slice(file.name.lastIndexOf(".") + 1)
    .toLowerCase();
  if (
    !file.name.includes(".") ||
    !PRODUCT_IMAGE_EXTENSIONS.includes(extension)
  ) {
    throw new Error(
      `지원하지 않는 형식입니다. ${PRODUCT_IMAGE_EXTENSIONS.join(", ").toUpperCase()} 만 업로드할 수 있습니다`,
    );
  }

  if (file.size > PRODUCT_IMAGE_MAX_BYTES) {
    throw new Error(
      `이미지가 너무 큽니다 (최대 ${Math.floor(PRODUCT_IMAGE_MAX_BYTES / 1024 / 1024)}MB)`,
    );
  }

  const formData = new FormData();
  formData.append("file", file);

  const response = await api<UploadEnvelope>({
    url: "/admin/items/upload-image",
    method: "POST",
    headers: { "Content-Type": "multipart/form-data" },
    data: formData,
  });

  const uploaded = response.data;
  if (!uploaded?.imageUrl) {
    throw new Error("이미지 업로드에 실패했습니다. 잠시 후 다시 시도해주세요");
  }

  return {
    imageUrl: uploaded.imageUrl,
    filePath: uploaded.filePath ?? "",
    fileName: uploaded.fileName ?? file.name,
    fileSize: uploaded.fileSize ?? file.size,
    mimeType: uploaded.mimeType ?? file.type,
  };
}

/**
 * 폼에 들어온 이미지 URL 이 실제로 <img> 에서 뜰 수 있는 주소인지 본다.
 *
 * 확장자까지 요구하지는 않는다 — 쿼리스트링으로 이미지를 내려주는 CDN 이 흔하기 때문이다.
 * 확실히 못 쓰는 것(`blob:`, `data:`, http(s) 아님)만 거른다.
 *
 * @returns 문제가 없으면 null, 있으면 사용자에게 보여줄 메시지
 */
export function validateProductImageUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("blob:")) {
    return "브라우저 임시 주소(blob:)는 저장할 수 없습니다. 파일 업로드 탭을 사용해주세요";
  }

  if (trimmed.startsWith("data:")) {
    return "데이터 URL 은 저장할 수 없습니다. 파일 업로드 탭을 사용해주세요";
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return "올바른 이미지 주소가 아닙니다 (http:// 또는 https:// 로 시작해야 합니다)";
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return "올바른 이미지 주소가 아닙니다 (http:// 또는 https:// 로 시작해야 합니다)";
  }

  // 상품 *상세 페이지* 링크를 이미지로 착각해 붙여넣는 사고가 잦다. HTML 로 보이는
  // 경로는 경고한다 — 확신할 수 없으므로 차단이 아니라 안내다.
  const path = parsed.pathname.toLowerCase();
  const looksLikeHtml =
    path.endsWith(".html") ||
    path.endsWith(".htm") ||
    path.endsWith(".php") ||
    path.endsWith("/");
  const hasImageExtension = PRODUCT_IMAGE_EXTENSIONS.some((ext) =>
    path.endsWith(`.${ext}`),
  );

  if (looksLikeHtml && !hasImageExtension) {
    return "상품 페이지 주소로 보입니다. 이미지에 마우스 오른쪽 → '이미지 주소 복사'로 얻은 주소를 넣어주세요";
  }

  return null;
}
